import type {
  SidebarIcons,
  SidebarSize,
  SymbolStyle,
  SymbolStyleMode,
  SymbolTint,
  ThemePreference,
} from '../session';

/**
 * THE WORDS OF THE APPEARANCE SETTINGS, ONCE (redesign register L15 and A8-7). Two places offer
 * them: the account menu (Light, Dark, Automatic, and a submenu per sidebar setting) and the
 * phone's More sheet, which keeps its one menu with all of them until the phone round (P6).
 * Every label is Apple's own word for the setting, because a person who has met the setting on
 * their Mac should not have to work out that we renamed it.
 */

/**
 * APPLE'S THREE WORDS, IN APPLE'S ORDER (Arif 2026-09-07). macOS System Settings lists Light,
 * Dark, Automatic and defaults to Automatic; "System" is the web's word for the third state and
 * it does not appear in this product.
 */
export const THEME_OPTIONS: readonly { readonly value: ThemePreference; readonly label: string }[] =
  [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'automatic', label: 'Automatic' },
  ];

/** Apple's four, in Apple's order (System Settings > Appearance > Icon & widget style). */
export const STYLE_OPTIONS: readonly { readonly value: SymbolStyle; readonly label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'dark', label: 'Dark' },
  { value: 'clear', label: 'Clear' },
  { value: 'tinted', label: 'Tinted' },
];

/** Apple's own words for the two mode families, in the order the pane lists them. */
export const MODE_LABELS: Record<SymbolStyleMode, string> = {
  always: 'Always',
  auto: 'Auto',
  light: 'Light',
  dark: 'Dark',
};

/**
 * BOTH WAYS A SIDEBAR ROW CAN LEAD (package A3, decision 3 of 2026-09-17). Apple's own word for
 * the artwork is "symbols" (SF Symbols, and hig: Sidebars calls them sidebar icons); "Tiles" is
 * this product's word for the coloured System Settings square it has drawn since E9.
 */
export const ICON_OPTIONS: readonly { readonly value: SidebarIcons; readonly label: string }[] = [
  { value: 'symbols', label: 'Symbols' },
  { value: 'tiles', label: 'Tiles' },
];

/**
 * TWO SIZES: "Small" is removed (redesign register R-S4 and L7: its 11px title sits under the
 * 13px floor). A stored Small reads as Medium (`normaliseSidebarSize`).
 */
export const SIZE_OPTIONS: readonly { readonly value: SidebarSize; readonly label: string }[] = [
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

export const TINT_LABELS: Record<SymbolTint, string> = {
  multicolour: 'Multicolour',
  blue: 'Blue',
  purple: 'Purple',
  pink: 'Pink',
  red: 'Red',
  orange: 'Orange',
  yellow: 'Yellow',
  green: 'Green',
  graphite: 'Graphite',
};

/**
 * The swatch a theme colour is picked by. It reuses the tile's own `tile-fill-*` classes, so a
 * swatch and the tile it produces cannot drift apart, and Multicolour - which is the absence of a
 * theme colour - gets the ring of all eight rather than a thirteenth colour invented for it.
 */
export const TINT_FILLS: Record<SymbolTint, string> = {
  multicolour: 'tint-swatch-multicolour',
  blue: 'tile-fill-blue',
  purple: 'tile-fill-purple',
  pink: 'tile-fill-pink',
  red: 'tile-fill-red',
  orange: 'tile-fill-orange',
  yellow: 'tile-fill-yellow',
  green: 'tile-fill-green',
  graphite: 'tile-fill-gray',
};

/** "Dark style", "Clear style": the heading of a style's own modes, as the menu has named it. */
export function styleModeHeading(style: SymbolStyle): string {
  return `${STYLE_OPTIONS.find((option) => option.value === style)?.label ?? ''} style`;
}
